<script lang="ts" setup>
	import type { SelectValue } from 'ant-design-vue/es/select'
	import type { Key } from 'ant-design-vue/lib/vc-table/interface'
	import type { HeadStatistics } from '@/components/Shared/head-statistics.vue'
	import usePurchaseApi from '~/services/apis/purchases-info'
	import StringMethods from '~/utils/character'
	import type { DynamicTableContext, DynamicTableRecord, TableColumn, HTMLTypeAttr, SelectOptionItem } from '@/types'

	type SubmissionContext = { [id: string]: number | string }
	type EventListeners = {
		validateEvent: {
			param: DynamicTableContext
			callback: (arg: boolean | Promise<boolean>) => void
			event: 'validator'
		}
	}
	type Props = {
		analysisSummary?: HeadStatistics
		tableRecords: DynamicTableContext[]
		tableColumns: TableColumn[]
		required?: boolean
		deletable?: boolean
		addNewField?: boolean
		loading?: boolean
		submitting?: boolean
		editable?: boolean
		useOptionMenu?: boolean
		verifyEmail?: boolean
		viewSlot?: boolean
		slotLabel?: string
		hasAnExistingSlot?: boolean
		onValidator?: (param: DynamicTableContext, callback: (arg: boolean | Promise<boolean>) => void) => void
	}
	const props = withDefaults(defineProps<Props>(), {
		verifyEmail: true
	})
	const { loading } = toRefs(props)
	const emits = defineEmits<{
		(event: 'update:tableRecords', param: DynamicTableContext[]): void
		(arg: 'done', param: unknown[]): void
		(arg: 'update:needToKnow', param: boolean): void
		(arg: 'update:viewSlot', param: boolean): void
		(arg: 'add-field', param?: unknown): void
		(arg: 'update-records', param: DynamicTableContext[]): void
		(event: 'validator', param: DynamicTableContext, callback: (arg: boolean | Promise<boolean>) => void): void
	}>()

	const { verifyTableUserRecord } = usePurchaseApi()
	const records = ref<DynamicTableContext[]>([])
	const formElement = ref<HTMLFormElement | null>(null)
	const togglingPolicy = shallowRef(false)
	const { stripUnderScore } = StringMethods()
	const { stringify } = useUtils()

	const isAnItemDone = computed((): boolean => {
		return Object.values(records.value).some(item => item.done)
	})

	const tableMenu = computed(() => {
		return {
			edit: {
				condition: true,
				callback: (record?: unknown[]) => {
					editRecord(record?.[0] as number)
				},
				spanClass: ''
			},
			'add new field': {
				condition: props.addNewField && isAnItemDone.value,
				callback: () => {
					validateForm().then(() => {
						emits('add-field')
					})
				},
				spanClass: 'text-primary-2 '
			},
			'remove field': {
				condition: props.deletable && records.value.length > 1,
				callback: (i?: unknown[]) => removeField(i?.[0] as number),
				spanClass: 'text-error-500'
			}
		}
	})

	const activeRecords = computed(() => {
		return props.tableRecords.filter(item => !item.isUsed)
	})

	async function validateForm(): Promise<{ [id: string]: DynamicTableContext }> {
		try {
			const res = await formElement.value?.validate()
			return Promise.resolve(res)
		} catch (error) {
			return Promise.reject(error)
		}
	}

	const isRecordEmpty = (obj: DynamicTableContext): boolean => {
		// Cache the object response to increase cpu process time
		const recordsCache = new Map<DynamicTableContext, boolean>()

		if (recordsCache.has(obj)) {
			return recordsCache.get(obj) || false
		}

		const { done, editable, isActive, sn, ...rest } = obj
		if (typeof rest === 'object') {
			for (const key in rest) {
				const res = rest[key]
				if (
					typeof res === 'object' &&
					(res?.value === undefined ||
						res?.value === null ||
						res.value === 'n/a' ||
						String(res.value).trim() === '')
				) {
					recordsCache.set(obj, true)
					return true
				}
			}
		}
		return false
	}

	const recordResponse = (record: DynamicTableContext, key: string) => {
		return record[key] as DynamicTableRecord
	}
	const getInputType = (
		record: DynamicTableContext,
		key: string
	): 'text' | 'password' | 'email' | 'tel' | 'number' => {
		const type = recordResponse(record, key)?.type
		if (type === 'password' || type === 'email' || type === 'tel' || type === 'number' || type === 'text') {
			return type
		}
		return 'text'
	}
	const getSelectOptions = (record: DynamicTableContext, key: string): SelectOptionItem[] => {
		const options = recordResponse(record, key)?.options
		if (Array.isArray(options)) {
			return options
		}
		if (options && typeof options === 'object') {
			return [options]
		}
		return []
	}
	const processRecords = () => {
		const dataForm: SubmissionContext[] = []
		const resultList = records.value.filter(item => item.done)
		// Structure the data to strip validation keys
		for (let index = 0; index < resultList.length; index++) {
			const record = resultList[index]
			const emptyRecord: SubmissionContext = {}
			for (const key in record) {
				const value = record[key]
				const valuesResult =
					typeof value === 'object' && value
						? (value as DynamicTableRecord).value
						: typeof value === 'boolean'
							? ''
							: value
				if (valuesResult) {
					Object.defineProperty(emptyRecord, key, {
						value: valuesResult,
						writable: true,
						enumerable: true
					})
				}
			}
			dataForm.push(emptyRecord)
		}
		return Promise.resolve(dataForm)
	}
	const isEmailAndPhoneLinked = async (payload: { email: string; phone: string }) => {
		if (props.verifyEmail) {
			const res = await verifyTableUserRecord(payload)
			if (res?.data) {
				const isLinked = res.data
				return isLinked
			}
		}
		return false
	}
	const validateTableField = (record: DynamicTableContext) => {
		const responseRecord = record as { [id: string]: DynamicTableRecord }
		const plateNumberVal = (responseRecord?.plate_number?.value as string) || ''
		const duplicatedRows = plateNumberVal ? hasDuplicatePlateNumber.value?.[plateNumberVal] || null : null

		if (duplicatedRows) {
			useNotification().alert({
				message: `You have a duplicated plate number at row ${duplicatedRows.join(
					', '
				)}, please use a different plate number`,
				status: 400
			})
			return null
		}
		for (const key in record) {
			if (responseRecord[key]?.validations) {
				let message = ''
				const validations = responseRecord[key].validations
				if (validations?.required && !responseRecord[key].value) {
					message = (validations?.message as string) || `${stripUnderScore(key)} is required`
				} else if (validations?.min && String(responseRecord[key].value).length < validations.min) {
					message =
						(validations?.message as string) ||
						`${stripUnderScore(key)} must be at least ${validations.min} characters long`
				} else if (validations?.max && String(responseRecord[key].value).length > validations?.max) {
					message =
						(validations?.message as string) ||
						`${stripUnderScore(key)} must be at less or equal to ${validations.min}`
				}
				if (message) {
					useNotification().alert({
						status: 400,
						message
					})
					return Promise.reject(message)
				}
				// else if (Number(responseRecord?.dependant?.value) > Number(responseRecord?.remaining_dependant)) {
				// 	const message = 'You have maxed out your dependant slot, reduce the value'
				// 	useNotification().alert({
				// 		status: 400,
				// 		message
				// 	})
				// 	return Promise.reject(message)
				// }
			}
		}
		return Promise.resolve(true)
	}

	const hasDuplicatePlateNumber = computed(() => {
		const plateNumberMap = records.value.reduce<Record<string, number[]>>((acc, record, index) => {
			const plateNumber = (record.plate_number as DynamicTableRecord)?.value as string | undefined
			if (plateNumber) {
				if (!acc[plateNumber]) {
					acc[plateNumber] = []
				}
				acc[plateNumber].push(index)
			}
			return acc
		}, {})

		const duplicates = Object.entries(plateNumberMap)
			.filter(([, indexes]) => indexes.length > 1)
			.reduce<Record<string, string[]>>((acc, [plateNumber, indexes]) => {
				acc[plateNumber] = indexes.map(String)
				return acc
			}, {})

		return duplicates
	})

	const isFieldValidated = (param: DynamicTableContext, fn: (res: boolean) => void) => {
		if (!props?.onValidator) {
			fn(true)
		}
		param.loading = true
		emits('validator', param, async response => {
			try {
				const result = await response
				if (result) {
					param.loading = false
					fn(true)
				}
			} catch (error) {
				console.warn(error)
			} finally {
				param.loading = false
			}
		})
	}

	function addField(record?: DynamicTableContext, index = 0) {
		const responseRecord = record as { [id: string]: DynamicTableRecord }

		const process = async () => {
			const canAdd = await validateTableField(responseRecord)

			resolve(async () => {
				if (record && canAdd) {
					const verificationPayload = {
						email: (responseRecord?.email?.value as string) || '',
						phone:
							(responseRecord?.phone?.value as string) ||
							(responseRecord?.phone_number?.value as string) ||
							'',
						role_number: Number(index) + 1
					}
					record.loading = true
					try {
						if (await isEmailAndPhoneLinked(verificationPayload)) {
							record.done = true
							record.isActive = false
							processRecords().then(records => {
								emits('add-field', records)
							})
							return null
						}
					} finally {
						record.loading = false
					}
				}
			})
		}
		isFieldValidated(responseRecord, async canProceed => {
			if (canProceed) {
				if (props.required) {
					validateForm().then(() => {
						process()
					})
					return null
				}
				if (record && !isRecordEmpty(record)) {
					await process()
					return null
				}
				useNotification().alert({
					status: 400,
					message: 'Field not added, something went wrong.'
				})
			}
		})
	}

	function editRecord(i: number) {
		const record = records.value[i]
		if (record) {
			record.done = false
			record.editable = true
		}
	}

	function removeField(index: number) {
		if (activeRecords.value.length > 1) {
			// @ts-ignore
			const filteredResult = records.value.filter((_, i) => i !== index)
			records.value = filteredResult
			emits('update-records', filteredResult)
		}
	}

	const updateRecord = (index: number) => {
		const record = records.value[index] as DynamicTableContext
		const verificationPayload = {
			email: ((record?.email as DynamicTableRecord)?.value as string) || '',
			phone: (((record?.phone || record?.phone_number) as DynamicTableRecord)?.value as string) || '',
			role_number: index + 1
		}
		isFieldValidated(record, async canProceed => {
			if (canProceed) {
				try {
					const canAdd = await validateTableField(record)
					if (canAdd) {
						record.loading = true
						if (!isRecordEmpty(record) && (await isEmailAndPhoneLinked(verificationPayload))) {
							record.isActive = false
							record.editable = false
							record.done = true
						}
					}
				} finally {
					record.loading = false
				}
			}
		})
	}
	const togglePolicy = () => {
		togglingPolicy.value = !togglingPolicy.value
		emits('update:viewSlot', togglingPolicy.value)
	}

	const saveForm = () => {
		if (props.required) {
			validateForm().then(() => {
				processRecords().then(dataForm => {
					emits('done', dataForm)
				})
			})
		} else {
			processRecords().then(dataForm => {
				emits('done', dataForm)
			})
		}
	}

	onMounted(() => {
		records.value = props.tableRecords
	})

	watch(
		() => props.tableRecords,
		result => {
			records.value = result
		},
		{
			deep: true
		}
	)
</script>

<template>
	<div class="w-full">
		<div class="flex-items flex w-full flex-col justify-center">
			<template v-if="analysisSummary">
				<shared-head-statistics
					:stats-details="analysisSummary"
					content-wrapper="border-primary-2  flex-1"
					class="border-primary-2 bg-primary-50"
				/>
				<button
					:class="{
						hidden: !hasAnExistingSlot,
						'md:flex': hasAnExistingSlot
					}"
					type="button"
					data-testid="add staff to plan"
					aria-label="add staff to plan"
					class="absolute right-0 top-[7rem] hidden items-center gap-3"
					@click="togglePolicy"
				>
					<span class="text-sm font-medium tracking-wide text-purple-500">
						{{ slotLabel ?? `View ${!viewSlot ? 'added staff' : 'empty slot'}` }}
					</span>
					<div
						class="flex h-[40px] w-[40px] flex-none items-center justify-center rounded-lg bg-purple-50 p-[10px_18px]"
					>
						<lazy-tools-icon dir="line" icon="user-add" />
					</div>
				</button>
				<slot name="note" />
				<button
					:class="{
						hidden: !hasAnExistingSlot,
						flex: hasAnExistingSlot
					}"
					type="button"
					data-testid="add staff to plan"
					aria-label="add staff to plan"
					class="ml- relative top-10 z-10 mb-5 items-center gap-3 md:hidden"
					@click="togglePolicy"
				>
					<span class="text-sm font-medium tracking-wide text-purple-500">
						View {{ !viewSlot ? 'added staff' : 'empty slot' }}
					</span>
					<div
						class="flex h-[40px] w-[40px] flex-none items-center justify-center rounded-lg bg-purple-50 p-[10px_18px]"
					>
						<lazy-tools-icon dir="line" icon="user-add" />
					</div>
				</button>
			</template>

			<shared-table-loader v-slot="{ columns }" :loading="loading" :columns="tableColumns">
				<a-form
					v-show="!viewSlot"
					ref="formElement"
					:model="records"
					class="w-full !max-w-full !px-0"
					autocomplete="off"
				>
					<shared-dynamic-table
						:loading="loading"
						:table-columns="columns"
						:table-record="records"
						class="w-full overflow-x-auto"
					>
						<template #body="{ index, column, record }">
							<div
								v-if="column.key === 'sn'"
								class="flex items-center justify-center font-medium"
								:class="{
									'bg-white': typeof record[column.key] === 'boolean',
									'min-h-[70px] text-gray-400': !record.done
								}"
							>
								{{ index + 1 }}
							</div>

							<template v-else-if="!['action', 'sn'].includes(column.key as string)">
								<template v-if="!record?.isActive && !record?.editable">
									<div
										v-if="column.key"
										class="flex min-h-[70px] w-full items-center px-5 text-gray-400 sm:min-w-[150px]"
									>
										<slot
											:name="`filled-${column.key}`"
											:record="record"
											:column="column.key"
											:value="recordResponse(record, column.key as string)?.value"
										>
											{{ recordResponse(record, column.key as string)?.value || 'N/A' }}
										</slot>
									</div>
								</template>

								<template v-else>
									<template v-if="typeof record[column.key as string] === 'object'">
										<a-form-item
											:name="[index, column.key as Key, 'value']"
											:required="recordResponse(record, column.key as string)?.required"
											:rules="[
												{
													type: column.key === 'email' ? 'email' : undefined
												},
												recordResponse(record, column.key as string)?.validations || {}
											]"
											class="error-absolute !mb-0"
										>
											<slot
												:name="column?.key"
												:record="record"
												:column="column"
												:index="index"
												:set-record="recordResponse"
											>
												<a-select
													v-if="
														recordResponse(record, column.key as string)?.type === 'select'
													"
													v-model:value="recordResponse(record, column.key as string).value"
													:options="getSelectOptions(record, column.key as string)"
													:disabled="recordResponse(record, column.key as string)?.disabled"
													:name="column.key as string"
													:aria-label="`${column.key}-${index}`"
													:data-testid="`${column.key}-${index}`"
													:placeholder="
														recordResponse(record, column.key as string)?.placeholder || '-'
													"
													:class="[
														recordResponse(record, column.key as string)?.class || '',
														'custom-select'
													]"
													:show-search="
														recordResponse(record, column.key as string)?.searchable
													"
													@change="
														(e: SelectValue) => {
															if (
																typeof recordResponse(record, column.key as string)
																	.onChange === 'function'
															) {
																;(
																	recordResponse(record, column.key as string)
																		.onChange as Function
																)(e, index)
															}
														}
													"
												/>

												<a-input
													v-else
													:id="column.key as string"
													v-model:value="recordResponse(record, column.key as string).value"
													v-number="
														['tel', 'value', 'phone'].includes(
															recordResponse(record, column.key as string)?.type || ''
														)
													"
													v-format="recordResponse(record, column.key as string)?.format"
													v-naira="
														recordResponse(record, column.key as string)?.currencyFormat
													"
													autocomplete="none"
													:aria-label="column.key"
													:data-testid="`${column.key}-${index}`"
													:maxlength="
														recordResponse(record, column.key as string)?.validations?.max
													"
													:type="getInputType(record, column.key as string)"
													:name="column.key as string"
													:placeholder="
														recordResponse(record, column.key as string)?.placeholder || '-'
													"
													:inputmode="
														recordResponse(record, column.key as string)?.inputMode ||
														'text'
													"
													:disabled="recordResponse(record, column.key as string)?.disabled"
													class="custom-input placeholder:text-center placeholder:font-extrabold placeholder:text-gray-950"
													:class="[recordResponse(record, column.key as string)?.class || '']"
													@change="
														(e: Event) => {
															if (
																typeof recordResponse(record, column.key as string)
																	.onChange === 'function'
															) {
																;(
																	recordResponse(record, column.key as string)
																		.onChange as Function
																)(e, index)
															}
														}
													"
												/>
											</slot>
										</a-form-item>
									</template>
								</template>
							</template>

							<!-- Table Actions -->
							<div v-else class="flex justify-center">
								<template v-if="!record.isUsed">
									<a-button
										v-if="record.isActive && !useOptionMenu"
										class="00 default-btn-sm !bg-primary-2 !text-white shadow-md disabled:!bg-primary-2 disabled:shadow-none"
										:disabled="isRecordEmpty(record)"
										:loading="record?.loading"
										:data-testid="`add-${index}`"
										@click.prevent="addField(record, index)"
									>
										<lazy-tools-icon dir="other" icon="plus" class="mr-2" />
										Add
									</a-button>
									<a-button
										v-else-if="
											(useOptionMenu &&
												!isRecordEmpty(record) &&
												index !== 0 &&
												record.isActive) ||
											(index == 0 && record.isActive)
										"
										:data-testid="`add-${index}`"
										:loading="record?.loading"
										class="00 default-btn-sm !bg-primary-2 !text-white shadow-md disabled:!bg-primary-2 disabled:shadow-none"
										:disabled="isRecordEmpty(record)"
										@click.prevent="addField(record)"
									>
										<lazy-tools-icon dir="other" icon="plus" class="mr-2" />
										Add
									</a-button>

									<a-dropdown
										v-if="useOptionMenu && !record.isActive && !record?.editable"
										trigger="click"
									>
										<div>
											<button
												type="button"
												:data-testid="`more-${index}`"
												class="inline-flex items-center justify-center rounded-[8px] p-[20px_33px] shadow-none"
											>
												<lazy-tools-icon icon="more" dir="system" />
											</button>
										</div>

										<template #overlay>
											<a-menu class="light-shadow w-full min-w-[120px] rounded-md !py-0">
												<template v-for="(v, k) in tableMenu" :key="k">
													<a-menu-item
														v-if="v?.condition"
														:data-testid="`${index}-${k}`"
														class="py-4 hover:bg-gray-25 hover:font-medium hover:!text-blue-950"
														@click="v.callback([index, null])"
													>
														<span class="w-full capitalize" :class="v?.spanClass">
															{{ k }}
														</span>
													</a-menu-item>
												</template>
											</a-menu>
										</template>
									</a-dropdown>

									<button
										v-if="!record.isActive && record.done && !useOptionMenu"
										type="submit"
										class="default-btn-sm bg-gray-400 text-white shadow-md disabled:bg-gray-200 disabled:shadow-none"
										:disabled="isRecordEmpty(record)"
										:data-testid="`edit-${index}`"
										@click="editRecord(index)"
									>
										<lazy-tools-icon dir="design" icon="edit" class="mr-2" />
										Edit
									</button>

									<button
										v-else-if="
											deletable &&
											tableRecords.length > 1 &&
											isRecordEmpty(record) &&
											index !== 0 &&
											!record.editable
										"
										type="button"
										:data-testid="`remove-${index}`"
										class="default-btn-sm bg-error-500 text-white disabled:bg-error-200"
										@click="removeField(index)"
									>
										<lazy-tools-icon dir="other" icon="recycle" class="mr-2" />
										Remove
									</button>

									<a-button
										v-else-if="!record.isActive && record.editable"
										class="default-btn-sm !bg-primary-2 !text-white hover:!bg-primary-500 disabled:!bg-primary-100"
										:disabled="isRecordEmpty(record)"
										:loading="record?.loading"
										:data-testid="`done-${index}`"
										@click="updateRecord(index)"
									>
										<lazy-tools-icon
											dir="fill"
											icon="check-2"
											:height="18"
											:width="18"
											path-class="stroke-white fill-transparent"
											class="mr-2"
										/>
										<span>Done</span>
									</a-button>
								</template>
								<button
									v-else
									type="button"
									:data-testid="`added-${index}`"
									class="0 default-btn-sm bg-primary-100 text-white"
									disabled
								>
									<lazy-tools-icon
										dir="fill"
										icon="check-2"
										:height="18"
										:width="18"
										path-class="stroke-white fill-transparent"
										class="mr-2"
									/>
									<span>Added</span>
								</button>
							</div>
							<!-- /=== -->
						</template>
					</shared-dynamic-table>

					<div
						class="fixed bottom-5 left-0 right-0 mx-auto flex w-full max-w-[564px] items-center justify-center px-5"
					>
						<a-button
							class="default-btn w-full justify-center bg-primary-2 text-white"
							:disabled="!isAnItemDone || loading || submitting"
							:loading="submitting"
							data-testid="add to plan"
							@click="saveForm"
						>
							{{ submitting ? 'Saving...' : 'Save and Continue' }}
						</a-button>
					</div>
				</a-form>
				<div v-show="viewSlot">
					<slot name="filled-slots" />
				</div>
			</shared-table-loader>
		</div>
	</div>
</template>

<style scoped>
	.custom-input {
		@apply !h-[70px] !rounded-none border-[0.5px] !border-transparent !px-5 focus:!border-primary-2 disabled:!bg-white disabled:!font-medium disabled:!text-gray-600;
	}

	.custom-select :deep(.ant-select-selector .ant-select-selection-item),
	.custom-select :deep(.ant-select-selection-search) {
		@apply flex !h-[70px] items-center;
	}

	.custom-select :deep(.ant-select-selector) {
		@apply !h-[70px] !rounded-none !border-none !bg-gray-100;
	}

	.custom-select :deep(.ant-select-selector .ant-select-selection-item) {
		@apply overflow-x-auto !text-gray-600;
	}

	.custom-select.ant-select-disabled :deep(.ant-select-selector .ant-select-selection-item) {
		@apply font-medium;
	}

	.custom-select.ant-select-disabled :deep(.ant-select-arrow) {
		@apply hidden;
	}

	.custom-select.ant-select-disabled :deep(.ant-select-selector) {
		@apply !bg-white;
	}

	.custom-select :deep(.ant-select-selection-placeholder) {
		@apply absolute bottom-0 top-0 my-auto h-[40px] w-full text-center font-normal leading-4 text-gray-400;
	}
</style>
